(function() {
  'use strict';

  const CONFIG = {
    containerSelector: '#Joy-PointCalculator__Custom--CartDrawer',
    pointsSelector: '.Joy-PointCalculator__Points',

    // Observe only this area (smaller scope = less work)
    cartRootSelector: '#CartDrawer'
  };

  const RETRY_CONFIG = {
    maxRetries: 12,
    delay: 120 // ms
  };

  // -----------------------------
  // Scheduling (FAST + FINAL)
  // -----------------------------
  let fastTimer = null;
  let finalTimer = null;

  function requestUpdate({fast = 160, final = 850} = {}) {
    clearTimeout(fastTimer);
    fastTimer = setTimeout(() => scheduleUpdatePoints(), fast);

    clearTimeout(finalTimer);
    finalTimer = setTimeout(() => scheduleUpdatePoints(), final);

    // Also observe DOM briefly (best-effort for late re-renders)
    // startShortDomWatch();
  }

  function formatPointBalance(num) {
    return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * IMPORTANT:
   * - Trigger updates only on cart MUTATIONS + section renders.
   * - DO NOT trigger on "/cart.js" because SDK autoFetchCart can call it => loops.
   */
  function isCartOrDrawerUrl(url) {
    if (!url) return false;
    const u = String(url);

    // Cart mutations (themes may use .js)
    if (
      u.includes('/cart/add') ||
      u.includes('/cart/change') ||
      u.includes('/cart/update') ||
      u.includes('/cart/clear') ||
      u.includes('/cart/add.js') ||
      u.includes('/cart/change.js') ||
      u.includes('/cart/update.js') ||
      u.includes('/cart/clear.js')
    )
      return true;

    // Section rendering (drawer/notification/combined sections)
    if (
      u.includes('section_id=cart-drawer') ||
      u.includes('sections=cart-drawer') ||
      u.includes('section_id=cart-notification') ||
      u.includes('sections=cart-notification') ||
      (u.includes('section_id=') && u.includes('cart')) ||
      (u.includes('sections=') && u.includes('cart'))
    )
      return true;

    return false;
  }

  function getJoyInstance() {
    return window.joyInstance || window.AVADA_JOY?.joyInstance || null;
  }

  async function waitForJoyInstance() {
    const existing = getJoyInstance();
    if (existing) return existing;

    return new Promise(resolve => {
      let settled = false;
      const settle = inst => {
        if (settled) return;
        settled = true;
        window.removeEventListener('joy:ready', onReady);
        clearInterval(interval);
        resolve(inst);
      };

      const onReady = () => settle(getJoyInstance());
      window.addEventListener('joy:ready', onReady);

      // Fallback poll in case joy:ready fired before we attached, or the SDK
      // never emits it on this page. No timeout cliff — keep checking quietly.
      const interval = setInterval(() => {
        const inst = getJoyInstance();
        if (inst) settle(inst);
      }, 200);
    });
  }

  async function calculateCartRewardPoints() {
    const joy = await waitForJoyInstance();
    if (!joy) return 0;

    try {
      const res = await joy.calculateCartPoints({autoFetchCart: true});
      if (res?.error) {
        console.warn('[Joy:PointCalculator] SDK error:', res.error);
        return 0;
      }
      return res?.points || 0;
    } catch (e) {
      console.error('[Joy:PointCalculator] Failed to calculate points', e);
      return 0;
    }
  }

  function animateCount(el, start, end, duration = 600) {
    const startTime = performance.now();

    function tick(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = Math.floor(start + (end - start) * eased);
      el.textContent = formatPointBalance(val);

      if (progress < 1) requestAnimationFrame(tick);
      else el.textContent = formatPointBalance(end);
    }

    requestAnimationFrame(tick);
  }

  async function updatePoints() {
    const containers = document.querySelectorAll(CONFIG.containerSelector);
    if (!containers.length) return;

    const newPoints = await calculateCartRewardPoints();

    containers.forEach(container => {
      const pointsEl = container.querySelector(CONFIG.pointsSelector);
      if (!pointsEl) return;

      const currentText = (pointsEl.textContent || '').replace(/,/g, '').trim();
      const currentValue = parseInt(currentText, 10) || 0;

      // Always enforce correct value (DOM can be re-rendered to "0")
      if (currentValue === newPoints) {
        pointsEl.textContent = formatPointBalance(newPoints);
      } else {
        animateCount(pointsEl, currentValue, newPoints);
      }
    });
  }

  function scheduleUpdatePoints(tries = RETRY_CONFIG.maxRetries) {
    const containers = document.querySelectorAll(CONFIG.containerSelector);
    if (!containers.length) {
      if (tries > 0) setTimeout(() => scheduleUpdatePoints(tries - 1), RETRY_CONFIG.delay);
      return;
    }

    const hasPoints = Array.from(containers).some(c => c.querySelector(CONFIG.pointsSelector));
    if (!hasPoints) {
      if (tries > 0) setTimeout(() => scheduleUpdatePoints(tries - 1), RETRY_CONFIG.delay);
      return;
    }

    updatePoints();
  }

  // -----------------------------
  // Short, scoped DOM watch (robust)
  // -----------------------------
  let domObserver = null;
  let domWatchStopTimer = null;
  let domWatchRafScheduled = false;

  function startShortDomWatch() {
    const root = document.querySelector(CONFIG.cartRootSelector);
    if (!root) return;

    // Start once
    if (!domObserver) {
      domObserver = new MutationObserver(() => {
        // throttle observer callback to 1 per frame
        if (domWatchRafScheduled) return;
        domWatchRafScheduled = true;

        requestAnimationFrame(() => {
          domWatchRafScheduled = false;

          // If our container exists now (or was replaced), update soon
          const container = document.querySelector(CONFIG.containerSelector);
          const pointsEl = container && container.querySelector(CONFIG.pointsSelector);

          if (pointsEl) {
            // If it looks like it got reset (often "0"), force a quick update
            // (still safe because we are NOT intercepting /cart.js)
            requestUpdate({fast: 60, final: 300});
          }
        });
      });

      domObserver.observe(root, {childList: true, subtree: true});
    }

    // Auto-stop observer shortly after things settle
    clearTimeout(domWatchStopTimer);
    domWatchStopTimer = setTimeout(stopDomWatch, 2000);
  }

  function stopDomWatch() {
    if (domObserver) {
      domObserver.disconnect();
      domObserver = null;
    }
    clearTimeout(domWatchStopTimer);
    domWatchStopTimer = null;
  }

  // -----------------------------
  // Interceptors
  // -----------------------------
  function hookCartRequests() {
    // XHR
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this._joyUrl = url;
      return originalXhrOpen.apply(this, arguments);
    };

    const originalXhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', () => {
        const url = this._joyUrl;
        if (url && isCartOrDrawerUrl(url)) {
          requestUpdate({fast: 160, final: 900});
        }
      });
      return originalXhrSend.apply(this, arguments);
    };

    // fetch
    const originalFetch = window.fetch;
    window.fetch = function(resource, options) {
      const url = typeof resource === 'string' ? resource : resource?.url;

      return originalFetch(resource, options).then(resp => {
        if (url && isCartOrDrawerUrl(url)) {
          requestUpdate({fast: 160, final: 900});
        }
        return resp;
      });
    };
  }

  function listenOnSectionLoad() {
    document.addEventListener('shopify:section:load', evt => {
      const id = evt?.detail?.sectionId;
      if (id && id.includes('cart')) {
        requestUpdate({fast: 120, final: 700});
      }
    });
  }

  function listenOnDrawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    let wasActive = drawer.classList.contains('active');
    const observer = new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) requestUpdate({fast: 120, final: 700});
      wasActive = isActive;
    });
    observer.observe(drawer, {attributes: true, attributeFilter: ['class']});
  }

  function init() {
    scheduleUpdatePoints(); // initial
    hookCartRequests();
    listenOnSectionLoad();
    listenOnDrawerOpen();

    // Ensure we don't keep observers running forever
    window.addEventListener('beforeunload', stopDomWatch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.JoyPointCalculator = {
    updatePoints,
    scheduleUpdatePoints,
    calculateCartRewardPoints
  };
})();
